import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';

const PaymentStatus = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const [status, setStatus] = useState('checking'); // checking, success, failed, error
  const [orderData, setOrderData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const MAX_POLLS = 30; // 30 polls * 2 seconds = 60 seconds max

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const checkPaymentStatus = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${API_BASE_URL}/api/orders/${orderId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        setStatus('error');
        return;
      }

      const data = await response.json();
      setOrderData(data);

      // Check order status
      if (data.status === 'paid' || data.status === 'assigned') {
        setStatus('success');
        // Redirect to success page after 2 seconds
        setTimeout(() => {
          navigate(`/dashboard/payment-success?order_id=${orderId}`);
        }, 2000);
      } else if (data.status === 'failed') {
        setStatus('failed');
        // Redirect to failed page after 2 seconds
        setTimeout(() => {
          navigate(`/dashboard/payment-failed?order_id=${orderId}`);
        }, 2000);
      } else if (pollCount >= MAX_POLLS) {
        // Timeout: still pending after max polls
        setStatus('timeout');
      }

    } catch (err) {
      console.error('Error checking payment status:', err);
      setStatus('error');
    }
  };

  useEffect(() => {
    if (!orderId) {
      navigate('/dashboard/payment-error?reason=missing_order_id');
      return;
    }

    // Initial check
    checkPaymentStatus();

    // Poll every 2 seconds
    const interval = setInterval(() => {
      setPollCount(prev => prev + 1);
      checkPaymentStatus();
    }, 2000);

    return () => clearInterval(interval);
  }, [orderId, pollCount]);

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#e0e7ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <Loader size={48} color="#667eea" className="animate-spin" />
            </div>

            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: 'var(--color-text-primary)',
              margin: '0 0 12px 0'
            }}>
              Verifying Payment
            </h1>

            <p style={{
              fontSize: '16px',
              color: 'var(--color-text-muted)',
              margin: '0 0 32px 0',
              lineHeight: '1.5'
            }}>
              Please wait while we confirm your payment...
            </p>

            <div style={{
              padding: '12px 16px',
              backgroundColor: 'var(--color-bg-inset)',
              borderRadius: '8px'
            }}>
              <div style={{
                width: '100%',
                height: '4px',
                backgroundColor: 'var(--color-border)',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${(pollCount / MAX_POLLS) * 100}%`,
                  height: '100%',
                  backgroundColor: 'var(--color-accent)',
                  transition: 'width 0.3s ease',
                  borderRadius: '2px'
                }} />
              </div>
              <p style={{
                fontSize: '12px',
                color: 'var(--color-text-muted)',
                margin: '8px 0 0 0',
                textAlign: 'center'
              }}>
                Checking status... ({pollCount}/{MAX_POLLS})
              </p>
            </div>
          </>
        );

      case 'success':
        return (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#d1fae5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <CheckCircle size={48} color="#10b981" />
            </div>

            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: 'var(--color-text-primary)',
              margin: '0 0 12px 0'
            }}>
              Payment Confirmed!
            </h1>

            <p style={{
              fontSize: '16px',
              color: 'var(--color-text-muted)',
              margin: '0 0 16px 0'
            }}>
              Redirecting to success page...
            </p>
          </>
        );

      case 'failed':
        return (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: 'var(--color-danger-bg-strong)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <XCircle size={48} color="#ef4444" />
            </div>

            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: 'var(--color-text-primary)',
              margin: '0 0 12px 0'
            }}>
              Payment Failed
            </h1>

            <p style={{
              fontSize: '16px',
              color: 'var(--color-text-muted)',
              margin: '0 0 16px 0'
            }}>
              Redirecting...
            </p>
          </>
        );

      case 'timeout':
        return (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: 'var(--color-warning-bg-strong)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <AlertCircle size={48} color="#f59e0b" />
            </div>

            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: 'var(--color-text-primary)',
              margin: '0 0 12px 0'
            }}>
              Status Check Timeout
            </h1>

            <p style={{
              fontSize: '16px',
              color: 'var(--color-text-muted)',
              margin: '0 0 24px 0',
              lineHeight: '1.5'
            }}>
              We're still processing your payment. Please check your order history in a few minutes.
            </p>

            <Button
              onClick={() => navigate('/dashboard/settings/orders')}
              style={{ width: '100%' }}
            >
              View Order History
            </Button>
          </>
        );

      case 'error':
        return (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: 'var(--color-danger-bg-strong)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <AlertCircle size={48} color="#ef4444" />
            </div>

            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: 'var(--color-text-primary)',
              margin: '0 0 12px 0'
            }}>
              Unable to Verify
            </h1>

            <p style={{
              fontSize: '16px',
              color: 'var(--color-text-muted)',
              margin: '0 0 24px 0',
              lineHeight: '1.5'
            }}>
              We couldn't retrieve your payment status. Please contact support if you were charged.
            </p>

            <Button
              onClick={() => navigate('/dashboard')}
              style={{ width: '100%' }}
            >
              Back to Dashboard
            </Button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 'calc(100vh - 48px)',
      padding: '20px'
    }}>
      <Card style={{
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        padding: '40px 30px'
      }}>
        {renderContent()}

        {orderId && (
          <div style={{
            marginTop: '24px',
            padding: '12px 16px',
            backgroundColor: 'var(--color-bg-inset)',
            borderRadius: '8px'
          }}>
            <p style={{
              fontSize: '12px',
              color: 'var(--color-text-muted)',
              margin: '0 0 4px 0'
            }}>
              Order ID
            </p>
            <p style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--color-text-primary)',
              margin: 0,
              fontFamily: 'monospace'
            }}>
              {orderId}
            </p>
          </div>
        )}
      </Card>

      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default PaymentStatus;
